import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useStore, getReportRows, getCashbook } from "@/lib/pos-store";
import { fmt, PageHeader } from "@/components/AppShell";
import { hasRole } from "@/lib/auth-store";
import {
  ArrowUpRight,
  TrendingUp,
  Wallet,
  Boxes,
  AlertTriangle,
} from "lucide-react";

export const Route = createFileRoute("/")({
  beforeLoad: ({ context }) => {
    // Viewers get redirected to their tab instead of the dashboard
    if (context.user?.role === "viewer") {
      throw redirect({ to: "/tab" });
    }
    // Waiters get redirected to their customer management page
    if (context.user?.role === "waiter") {
      throw redirect({ to: "/tabs-waiter" });
    }
  },
  head: () => ({
    meta: [
      { title: "WH Lounge — Today" },
      {
        name: "description",
        content: "Real-time sales dashboard for your shop.",
      },
    ],
  }),
  component: Home,
});

function Home() {
  const state = useStore((s) => s);
  const rows = getReportRows(state);
  const cb = getCashbook(state);
  const totalSales = rows.reduce((a, b) => a + b.totalAmount, 0);
  const totalProfit = rows.reduce((a, b) => a + b.totalProfit, 0);
  const lowStock = state.items.filter((i) => i.stock <= 5);

  const recent = state.sales.slice(0, 6);

  return (
    <div>
      <PageHeader
        title="Today"
        subtitle={new Date().toLocaleDateString("en-KE", {
          weekday: "long",
          day: "numeric",
          month: "short",
        })}
      />

      <section className="px-4 pt-4">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary to-primary/70 p-5 text-primary-foreground shadow-lg">
          <p className="text-xs uppercase tracking-widest opacity-80">
            Sales today
          </p>
          <p className="mt-1 font-display text-4xl font-bold">
            {fmt(totalSales)}
          </p>
          <div className="mt-4 flex items-center gap-1 text-sm">
            <TrendingUp className="h-4 w-4" />
            <span>{fmt(totalProfit)} profit</span>
            <span className="opacity-60">· {state.sales.length} txns</span>
          </div>
          <Boxes className="absolute -right-4 -top-4 h-32 w-32 opacity-10" />
        </div>
      </section>

      <section className="grid grid-cols-2 gap-3 px-4 pt-4">
        <Stat label="M-Pesa" value={fmt(cb.mpesa)} />
        <Stat
          label="Cash"
          value={fmt(cb.cashHandedOver + cb.purchases + cb.expenditure)}
        />
        <Stat label="Banking" value={fmt(cb.banking)} />
        <Stat
          label="Debts"
          value={fmt(cb.debts)}
          tone={cb.debts > 0 ? "warn" : undefined}
        />
      </section>

      <section className="px-4 pt-6">
        <div className="grid grid-cols-2 gap-3">
          <QuickLink
            to="/sell"
            label="New Sale"
            icon={<ArrowUpRight className="h-5 w-5" />}
            primary
          />
          <QuickLink
            to="/cashbook/new"
            label="Record Cash"
            icon={<Wallet className="h-5 w-5" />}
          />
        </div>
      </section>

      {lowStock.length > 0 && (
        <section className="px-4 pt-6">
          <div className="flex items-center gap-2 rounded-xl border border-warning/50 bg-warning/10 p-3 text-warning-foreground">
            <AlertTriangle className="h-4 w-4 text-warning" />
            <p className="text-sm">
              <span className="font-semibold">{lowStock.length}</span> item
              {lowStock.length > 1 ? "s" : ""} running low
            </p>
            <Link
              to="/inventory"
              className="ml-auto text-xs font-semibold underline"
            >
              View
            </Link>
          </div>
        </section>
      )}

      <section className="px-4 pt-6">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="font-display text-base font-semibold">Recent sales</h2>
          <Link to="/reports" className="text-xs font-medium text-primary">
            All reports →
          </Link>
        </div>
        <div className="space-y-2">
          {recent.map((s) => {
            const item = state.items.find((i) => i.id === s.itemId);
            return (
              <Link
                to="/receipt/$id"
                params={{ id: s.id }}
                key={s.id}
                className="flex items-center justify-between rounded-xl border border-border bg-card p-3 active:scale-[0.98]"
              >
                <div>
                  <p className="text-sm font-medium">{item?.name ?? "—"}</p>
                  <p className="text-xs text-muted-foreground">
                    {s.qty} × {fmt(s.unitPrice)} · {s.payment.toUpperCase()}
                  </p>
                </div>
                <p className="font-display text-sm font-semibold">
                  {fmt(s.total)}
                </p>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "warn";
}) {
  return (
    <div
      className={`rounded-xl border border-border bg-card p-3 ${tone === "warn" ? "border-warning/40" : ""}`}
    >
      <p className="text-xs uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className="mt-0.5 font-display text-lg font-semibold">{value}</p>
    </div>
  );
}

function QuickLink({
  to,
  label,
  icon,
  primary,
}: {
  to: string;
  label: string;
  icon: React.ReactNode;
  primary?: boolean;
}) {
  return (
    <Link
      to={to as any}
      className={`flex items-center justify-between rounded-xl p-4 text-sm font-medium shadow-sm active:scale-[0.98] min-h-[52px] ${
        primary
          ? "bg-primary text-primary-foreground"
          : "border border-border bg-card"
      }`}
    >
      {label}
      {icon}
    </Link>
  );
}
